import React from 'react';
import { Drawer } from 'components/UI/V3/Drawer/Drawer';
import { DrawerHeader } from 'components/UI/V3/Drawer/Header/DrawerHeader';
import { DrawerBody } from 'components/UI/V3/Drawer/Body/DrawerBody';
import { DrawerFooter } from 'components/UI/V3/Drawer/Footer/DrawerFooter';
import Button from 'components/UI/V3/Button/Button';
import styles from './LeadFiltersDrawer.module.scss';
import { Period } from '@spike/model';
import { useDateRangeOptions } from 'hooks/useDateRangeOptions';
import { useTimeZone } from 'hooks';
import { sourceOptions } from '../Form/LeadForm';
import { FilterDateRangeRow } from './FilterDateRangeRow';
import { FilterMultiSelectRow } from './FilterMultiSelectRow';
import { FilterDrawerState, useLeadsUI } from '../LeadsProvider';
import { RadioGroup } from 'components/UI/V3/Forms/RadioGroup';

export interface LeadFiltersDrawerProps {
    onClose: () => void;
}

const STAGE_OPTIONS = ['New', 'Contacted', 'Booked', 'Won', 'Lost'];

export const LeadFiltersDrawer: React.FC<LeadFiltersDrawerProps> = ({ onClose }) => {
    const { handleApplyFilterDrawer, handleClearFilterDrawer, draftFilters, setDraftFilters } = useLeadsUI();

    const filters = draftFilters;
    const setFilters = setDraftFilters;

    const timeZone = useTimeZone();

    const {
        todayOption,
        thisMonthOption,
        last30DaysOption,
        allTimeOption,
        yesterdayOption,
        last7DaysOption,
        lastMonthOption
    } = useDateRangeOptions();

    // When enabled with period=null the user explicitly chose "All time" → keep null.
    // Only fall back to today when the filter has never been enabled (fresh default).
    const [creationDateRange, setCreationDateRange] = React.useState<Period | null>(
        filters.creationDate.enabled ? filters.creationDate.period : todayOption.value
    );
    const [lastContactedDateRange, setLastContactedDateRange] = React.useState<Period | null>(
        filters.lastContacted.enabled ? filters.lastContacted.period : todayOption.value
    );

    const handleFilterToggle = (filterKey: 'creationDate' | 'lastContacted' | 'source' | 'stage') => {
        setFilters((prev: FilterDrawerState) => {
            const current = (prev as any)[filterKey];
            const turningOff = current.enabled;
            const reset =
                turningOff && filterKey === 'source'
                    ? { selectedSources: [] }
                    : turningOff && filterKey === 'stage'
                    ? { selectedStages: [] }
                    : {};
            return {
                ...prev,
                [filterKey]: {
                    ...current,
                    enabled: !current.enabled,
                    ...reset
                }
            };
        });
    };

    const handleSourceToggleList = (sources: string[]) => {
        setFilters((prev: FilterDrawerState) => ({
            ...prev,
            source: {
                ...prev.source,
                selectedSources: sources
            }
        }));
    };

    const handleStageToggleList = (stages: string[]) => {
        setFilters((prev: FilterDrawerState) => ({
            ...prev,
            stage: {
                ...prev.stage,
                selectedStages: stages
            }
        }));
    };

    const isApplyDisabled = React.useMemo(() => {
        const anyFilterEnabled =
            filters.creationDate.enabled ||
            filters.lastContacted.enabled ||
            filters.source.enabled ||
            filters.stage.enabled;

        if (!anyFilterEnabled) return true;

        if (filters.source.enabled && filters.source.selectedSources.length === 0) {
            return true;
        }

        if (filters.stage.enabled && filters.stage.selectedStages.length === 0) {
            return true;
        }

        return false;
    }, [filters]);

    const handleApplyFilters = () => {
        if (isApplyDisabled) return;

        const toApply: FilterDrawerState = {
            ...filters,
            creationDate: {
                ...filters.creationDate,
                period: creationDateRange
            },
            lastContacted: {
                ...filters.lastContacted,
                period: lastContactedDateRange
            }
        };

        handleApplyFilterDrawer(toApply);
        onClose();
    };

    const handleClearFilters = () => {
        setCreationDateRange(todayOption.value);
        setLastContactedDateRange(todayOption.value);
        handleClearFilterDrawer(); // resets both draft and applied, does NOT close
    };

    const filterOperatorOptions: Array<{ value: 'and' | 'or'; inlineLabel: string }> = [
        { value: 'and', inlineLabel: 'And' },
        { value: 'or', inlineLabel: 'Or' }
    ];

    return (
        <Drawer onClose={onClose}>
            <DrawerHeader title="Filter Leads" />
            <DrawerBody>
                <div className={styles.root}>
                    <div className={styles.section}>
                        <div className={styles.row}>
                            <RadioGroup
                                label="Filter Type"
                                options={filterOperatorOptions}
                                value={filters.filterOperator}
                                onChange={(value: 'and' | 'or') =>
                                    setFilters((prev: FilterDrawerState) => ({
                                        ...prev,
                                        filterOperator: value
                                    }))
                                }
                            />
                            <div className={styles.description}>Refine your list by applying the filters below.</div>
                        </div>

                        {/* By Creation Date */}
                        <FilterDateRangeRow
                            label="By Creation Date"
                            enabled={filters.creationDate.enabled}
                            onToggle={() => handleFilterToggle('creationDate')}
                            timeZone={timeZone}
                            value={creationDateRange}
                            onChange={val => setCreationDateRange(val)}
                            options={[
                                todayOption,
                                yesterdayOption,
                                last7DaysOption,
                                thisMonthOption,
                                last30DaysOption,
                                lastMonthOption,
                                allTimeOption
                            ]}
                            dropdownProps={{ minWidth: 390, maxWidth: 520 }}
                        />

                        {/* By Source */}
                        <FilterMultiSelectRow
                            label="By Source"
                            placeholder="Source"
                            enabled={filters.source.enabled}
                            onToggle={() => handleFilterToggle('source')}
                            options={sourceOptions.map(o => ({ id: o.value, name: o.label }))}
                            selectedIds={filters.source.selectedSources}
                            onChange={handleSourceToggleList}
                        />

                        {/* By Last Contacted */}
                        <FilterDateRangeRow
                            label="By Last Contacted"
                            enabled={filters.lastContacted.enabled}
                            onToggle={() => handleFilterToggle('lastContacted')}
                            timeZone={timeZone}
                            value={lastContactedDateRange}
                            onChange={val => setLastContactedDateRange(val)}
                            options={[
                                todayOption,
                                yesterdayOption,
                                last7DaysOption,
                                thisMonthOption,
                                last30DaysOption,
                                lastMonthOption,
                                allTimeOption
                            ]}
                            dropdownProps={{ minWidth: 500, maxWidth: 520 }}
                        />

                        {/* By Stage */}
                        <FilterMultiSelectRow
                            label="By Stage"
                            placeholder="Stage"
                            enabled={filters.stage.enabled}
                            onToggle={() => handleFilterToggle('stage')}
                            options={STAGE_OPTIONS.map(s => ({ id: s, name: s }))}
                            selectedIds={filters.stage.selectedStages}
                            onChange={handleStageToggleList}
                        />
                    </div>
                </div>
            </DrawerBody>

            <DrawerFooter className={styles.footer}>
                <Button
                    label="Clear Filter"
                    onClick={handleClearFilters}
                    variant="black"
                    size="large"
                />
                <Button
                    label="Add Filter"
                    onClick={handleApplyFilters}
                    disabled={isApplyDisabled}
                    variant="green"
                    size="large"
                />
            </DrawerFooter>
        </Drawer>
    );
};

export default LeadFiltersDrawer;
